import { Component } from "react";
import { useEffect } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import Lenis from "lenis";
import "@/App.css";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import Approche from "@/components/Approche";
import CommentCaSePasse from "@/components/CommentCaSePasse";
import Soins from "@/components/Soins";
import Tarifs from "@/components/Tarifs";
import Lieu from "@/components/Lieu";
import Medecin from "@/components/Medecin";
import FAQ from "@/components/FAQ";
import RendezVous from "@/components/RendezVous";
import Footer from "@/components/Footer";
import MentionsLegales from "@/pages/MentionsLegales";
import SoinPage from "@/pages/SoinPage";
import TarifsPage from "@/pages/TarifsPage";
import MedecinPage from "@/pages/MedecinPage";
import CabinetPage from "@/pages/CabinetPage";
import ConseilsPage from "@/pages/ConseilsPage";
import NotFound from "@/pages/NotFound";
import useSeo from "@/hooks/useSeo";
import useI18n from "@/i18n";
import { LANGS, LEGAL_PATH, pagePath } from "@/i18n/config";

class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    componentDidCatch(error, info) {
        // Trace complète dans la console pour pouvoir diagnostiquer.
        console.error("[Aurassium] Erreur de rendu :", error, info?.componentStack);
    }
    render() {
        if (this.state.hasError) {
            const { texts, homePath } = this.props;
            return (
                <div
                    className="mx-auto max-w-[520px] px-5 py-24 text-center"
                    data-testid="error-boundary"
                >
                    <h1 className="font-display text-3xl text-terrat">{texts.h1}</h1>
                    <hr className="rule-gold mx-auto mt-5 w-16" />
                    <p className="mt-5 text-ink2">{texts.text}</p>
                    <div className="mt-8 flex flex-wrap justify-center gap-5">
                        <button
                            type="button"
                            onClick={() => window.location.reload()}
                            className="btn btn-primary"
                            data-testid="error-reload-button"
                        >
                            {texts.reload}
                        </button>
                        <a
                            href={homePath}
                            className="link-underline font-bold text-terrat"
                            data-testid="error-home-link"
                        >
                            {texts.home}
                        </a>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

/* Une route par langue : le français garde ses adresses, l'anglais et
 * l'espagnol vivent sous /en/ et /es/ avec des adresses traduites. */
const PAGE_ROUTES = [
    ["home", <Accueil />],
    ["tarifs", <TarifsPage />],
    ["medecin", <MedecinPage />],
    ["cabinet", <CabinetPage />],
    ["conseils", <ConseilsPage />],
    ["notfound", <NotFound />],
];

/* La barrière d'erreur est remontée à chaque changement d'URL : une erreur
 * ponctuelle ne doit pas bloquer toute la navigation jusqu'au rechargement. */
function RoutesAvecBarriere() {
    const { pathname } = useLocation();
    const { t, lang } = useI18n();
    return (
        <ErrorBoundary key={pathname} texts={t.error} homePath={pagePath(lang, "home")}>
            <Routes>
                {LANGS.map((l) => (
                    <Route key={`${l}-soin`} path={`${pagePath(l, "home")}/:slug`.replace("//", "/")} element={<SoinPage />} />
                ))}
                {LANGS.flatMap((l) =>
                    PAGE_ROUTES.map(([key, element]) => (
                        <Route key={`${l}-${key}`} path={pagePath(l, key)} element={element} />
                    )),
                )}
                <Route path={LEGAL_PATH} element={<MentionsLegales />} />
                <Route path="*" element={<NotFound />} />
            </Routes>
        </ErrorBoundary>
    );
}

function Accueil() {
    const { t, lang } = useI18n();
    useSeo({
        title: t.seo.home.title,
        description: t.seo.home.description,
        path: pagePath(lang, "home"),
        lang,
        pageKey: "home",
    });

    const { hash } = useLocation();
    useEffect(() => {
        if (!hash) return;
        /* Un fragment d'URL exotique rendrait querySelector invalide. */
        let cible = null;
        try {
            cible = document.querySelector(hash);
        } catch {
            return;
        }
        if (cible) requestAnimationFrame(() => cible.scrollIntoView({ block: "start" }));
    }, [hash]);

    return (
        <>
            <Header />
            <main className="pt-[104px] md:pt-16">
                <Hero />
                <Approche />
                <CommentCaSePasse />
                <Soins />
                <Tarifs />
                <Lieu />
                <Medecin />
                <FAQ />
                <RendezVous />
            </main>
            <Footer />
        </>
    );
}

function App() {
    useEffect(() => {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
        const lenis = new Lenis({ duration: 0.9, smoothWheel: true });
        window.__lenis = lenis;
        let raf;
        const loop = (t) => {
            lenis.raf(t);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
            window.__lenis = null;
        };
    }, []);

    return (
        <BrowserRouter>
            <RoutesAvecBarriere />
        </BrowserRouter>
    );
}

export default App;
